import { getCourseLearningResource, getLectureNoteAuthPath, getOtherPartnerMarkLink } from '@/apis';
import { LiveStatus, SaasCourseType, type ICourseListItem, type IResourcesCoverListItem } from '@/types';
import { environment } from '@gaodun.com/gtools';

export const alertBeforeIcon = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/03/bea2f_20240703173257.png';
export const alertAfterIcon = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/03/2f486_20240703173330.png';
export const playIcon = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/23/72267_20240723110500.png';

export const emptySrc = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/09/f6f9f_20240709152344.png';

const defaultCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/15/a6d1d_20240715162501.png';
const softLinkCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/15/0040a_20240715162443.png';
const liveNewCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/15/aa7d6_20240715162516.png';
const lectureNoteCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/15/409d3_20240715162527.png';
const paperCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/15/02a89_20240715162551.png';
const videoCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/15/f78ab_20240715162559.png';
const smartSchoolCover = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/23/12a8b_20240723110026.png';

export const getCourseCover = (resourcesCover: IResourcesCoverListItem[], course: ICourseListItem) => {
    const resourceCover = resourcesCover?.find(resource => resource.resourceId === course.currentStudyResourceId);
    if (!resourceCover) {
        return {
            src: defaultCover,
            isDefault: true,
        };
    }

    switch (resourceCover.discriminator) {
        case 'video':
            return {
                src: resourceCover.cover || videoCover,
                isDefault: !resourceCover.cover,
            };
        case 'paper':
            return {
                src: resourceCover.cover || paperCover,
                isDefault: !resourceCover.cover,
            };
        case 'lecture_note':
            return {
                src: resourceCover.cover || lectureNoteCover,
                isDefault: !resourceCover.cover,
            };
        case 'legacy_live':
        case 'live_playback_link':
        case 'live_new':
            return {
                src: resourceCover.cover || liveNewCover,
                isDefault: !resourceCover.cover,
            };
        case 'soft_link':
            return {
                src: resourceCover.cover || softLinkCover,
                isDefault: !resourceCover.cover,
            };
        case 'smart_school':
            return {
                src: resourceCover.cover || smartSchoolCover,
                isDefault: !resourceCover.cover,
            };
        default:
            return {
                src: defaultCover,
                isDefault: true,
            };
    }
};

/* 跳转第三方项目课程 */
export const goToOtherPartner = async ({ otherPartnerMark, saasCourseId }: ICourseListItem) => {
    const response = await getOtherPartnerMarkLink({
        otherPartnerMark: otherPartnerMark,
        courseId: saasCourseId,
    });
    if (response.status === 0 && response.result.url) {
        window.open(response.result.url, '_blank');
    }
};

export const goToSyllabus = (course: ICourseListItem) => {
    const env = environment.getApiEnv();
    let syllabusUrl = '';
    switch (course.saasCourseType) {
        case SaasCourseType.NORMAL2:
            syllabusUrl = `https://${env}cloud.gaodun.com/syllabus/${course.saasCourseId}`;
            break;
        case SaasCourseType.EP3:
            syllabusUrl = `https://${env}epiphany.gaodun.com/ep3/course/${course.saasCourseId}/syllabus`;
            break;
        case SaasCourseType.GLIVE2:
            syllabusUrl = `https://${env}glivepro.gaodun.com/course/${course.saasCourseId}/class-schedule`;
            break;
        case SaasCourseType.EP_LUCA:
            syllabusUrl = `https://${env}ep-luca.gaodun.com/course/${course.saasCourseId}/home-page`;
            break;
        default:
            break;
    }
    if (syllabusUrl) {
        window.open(syllabusUrl, '_blank');
    }
};

export const fetchCourseCurrentLearningResource = async (course: ICourseListItem) => {
    const response = await getCourseLearningResource({ course_id: course.saasCourseId });
    if (response.status !== 0 || !response.result) {
        goToSyllabus(course);
        return;
    }

    switch (course.saasCourseType) {
        case SaasCourseType.NORMAL2:
            _goToNormal2Study(response.result, course);
            break;
        case SaasCourseType.EP3:
            _goToEp3Study(response.result, course);
            break;
        case SaasCourseType.GLIVE2:
            _goToGLive2Study(response.result, course);
            break;
        case SaasCourseType.EP_LUCA:
            _goToEpLucaStudy(response.result, course);
            break;
        default:
            break;
    }
};

// 跳转liveNew直播
const _goToLiveNew = (data: any, course: ICourseListItem, path?: string) => {
    const { resource } = data;
    const ua = navigator.userAgent.toLocaleLowerCase();
    switch (resource?.status_of_live) {
        case LiveStatus.live:
        case LiveStatus.preLive:
            window.open(resource.url_live_is_playing_pc, '_blank');
            break;
        case LiveStatus.onlinePlayback:
            window.open(resource.url_live_play_back_pc, '_blank');
            break;
        case LiveStatus.clipPlayback:
            if (path) {
                window.open(path, '_blank');
            } else {
                goToSyllabus(course);
            }
            break;
        default:
            goToSyllabus(course);
            break;
    }
};

// 跳转讲义加密地址
const _goToLectureNotePath = async (data: any) => {
    const {
        resource_id,
        ori_resource: { title, extension },
    } = data;
    const response = await getLectureNoteAuthPath({ resource_id: resource_id, filename: `${encodeURIComponent(title)}.${extension}` });
    if (response.status === 0 && response.result) {
        return response.result;
    }
    return '';
};

const _normal2ResourceTypeMap: any = {
    video: 'play',
    lecture_note: 'handout',
    live_new: 'g2play',
    'paper/exercise': 'paper/exercise',
    'paper/report': 'paper/report',
};

const _goToNormal2Study = (data: any, course: ICourseListItem) => {
    const { resource, course_id, gradation_id, syllabus_id, part_id, cs_item_id, resource_id, soft_link_url, ori_resource } = data;
    const env = environment.getApiEnv();
    const path = `https://${env}cloud.gaodun.com/study/${_normal2ResourceTypeMap[resource]}?course_id=${course_id}&gradation_id=${gradation_id}&syllabus_id=${syllabus_id}&syllabusitem_id=${cs_item_id}&resource_id=${resource_id}&partId=${part_id}`;

    if (['video', 'lecture_note'].includes(resource)) {
        window.open(path, '_blank');
        return;
    }

    if (['paper/exercise', 'paper/report'].includes(resource)) {
        window.open(path + `&paper_id=${ori_resource.paper_id}`, '_blank');
        return;
    }

    if (resource === 'live_new') {
        _goToLiveNew({ resource: data }, course, path);
        return;
    }

    if (resource === 'soft_link' && soft_link_url) {
        window.open(soft_link_url, '_blank');
        return;
    }
    goToSyllabus(course);
};

const _ep3ResourceTypeMap: any = {
    video: 1,
    'paper/exercise': 2,
    lecture_note: 7,
    live_new: 8,
};

const _goToEp3Study = (data: any, course: ICourseListItem) => {
    const {
        resource,
        course_id,
        parent_gradation_id,
        gradation_id,
        syllabus_id,
        part_id,
        cs_item_id,
        resource_id,
        teacher_id,
        soft_link_url,
    } = data;
    const env = environment.getApiEnv();
    const path = `https://${env}epiphany.gaodun.com/ep3/course/${course_id}/learning/${_ep3ResourceTypeMap[resource]}/${
        parent_gradation_id ? parent_gradation_id : gradation_id
    }/${parent_gradation_id ? gradation_id : 0}/${syllabus_id}/${part_id}/${cs_item_id}/${resource_id}/${teacher_id || 0}`;

    if (['video', 'lecture_note', 'paper/exercise'].includes(resource)) {
        window.open(path, '_blank');
        return;
    }

    if (resource === 'live_new') {
        _goToLiveNew({ resource: data }, course, path);
        return;
    }

    if (resource === 'soft_link' && soft_link_url) {
        window.open(soft_link_url, '_blank');
        return;
    }

    goToSyllabus(course);
};

const _goToGLive2Study = async (data: any, course: ICourseListItem) => {
    const {
        resource,
        course_id,
        gradation_id,
        syllabus_id,
        cs_item_id,
        resource_id,
        soft_link_url,
        title,
        progress,
        ori_resource,
        chapter_id,
        chapter_test,
        course_syllabus_type, // 2是g2大纲
    } = data;
    const env = environment.getApiEnv();
    const basePath = `https://${env}glivepro.gaodun.com/course/${course_id}`;
    const path =
        course_syllabus_type === 2
            ? `${basePath}/syllabus_id/${syllabus_id}/chapter_id/0/item_id/${cs_item_id}/resource_id/${resource_id}/type/2?gradation_id=${gradation_id}`
            : `${basePath}/syllabus_id/${syllabus_id}/chapter_id/${chapter_id}/item_id/${cs_item_id}/resource_id/${resource_id}/type/1?gradation_id=${gradation_id}&child_gradation_id=0&teacher_id=0`;

    if (resource === 'paper') {
        window.open(
            `${basePath}/tiku-resource/cs_item_id/${cs_item_id}/resource_id/${resource_id}/paper_id/${ori_resource.paper_id}/type/${
                course_syllabus_type === 2 ? 2 : 1
            }/resState/${progress}?title=${encodeURIComponent(
                ori_resource.title
            )}&stageId=${gradation_id}&chapterTest=${chapter_test}&chapterId=${chapter_id}`,
            '_blank'
        );
        return;
    }

    if (resource === 'lecture_note') {
        const authPath = await _goToLectureNotePath(data);
        if (authPath) {
            window.open(
                `https://${env}glivepro.gaodun.com/handout-preview?resource=${encodeURIComponent(authPath)}&name=${ori_resource.title}`,
                '_blank'
            );
            return;
        }

        goToSyllabus(course);
        return;
    }

    if (['video'].includes(resource)) {
        window.open(path, '_blank');
        return;
    }

    if (resource === 'live_new') {
        _goToLiveNew({ resource: data }, course, path);
        return;
    }

    if (resource === 'soft_link' && soft_link_url) {
        window.open(soft_link_url, '_blank');
        return;
    }

    if (resource === 'smart_school' && ori_resource.playback_mode === 0 && ori_resource.playback_link) {
        window.open(path, '_blank');
        return;
    }

    goToSyllabus(course);
};

const _goToEpLucaStudy = (data: any, course: ICourseListItem) => {
    const { resource, course_id, gradation_id, syllabus_id, chapter_id, cs_item_id, resource_id, soft_link_url } = data;
    const env = environment.getApiEnv();
    const path = `https://${env}ep-luca.gaodun.com/course/${course_id}/${resource}/${gradation_id}/${syllabus_id}/${chapter_id}/${cs_item_id}/${resource_id}`;

    if (['video', 'paper'].includes(resource)) {
        window.open(path, '_blank');
        return;
    }

    if (resource === 'live_new') {
        _goToLiveNew({ resource: data }, course);
        return;
    }

    if (resource === 'soft_link' && soft_link_url) {
        window.open(soft_link_url, '_blank');
        return;
    }
    goToSyllabus(course);
};
